import { createSlice, createSelector } from '@reduxjs/toolkit';

/** Recalculate a line item's amount from rate, qty, and discount. */
function calcAmount(item) {
  const gross = item.qty * item.rate;
  const discountAmt = (gross * (item.discount_percentage || 0)) / 100;
  item.amount = gross - discountAmt;
}

const initialState = {
  items: [],       // [{ item_code, item_name, qty, rate, amount, discount_percentage, note, uom, stock_uom, item_image }]
  customer: null,  // { name, customer_name } | null
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    /**
     * Add an item to the cart.
     * If the item already exists (by item_code), increment its qty.
     */
    addItem(state, action) {
      const incoming = action.payload;
      const existing = state.items.find((i) => i.item_code === incoming.item_code);

      if (existing) {
        existing.qty += 1;
        calcAmount(existing);
      } else {
        const newItem = {
          item_code: incoming.item_code,
          item_name: incoming.item_name,
          qty: 1,
          rate: incoming.price_list_rate || incoming.rate || 0,
          amount: 0,
          discount_percentage: 0,
          note: '',
          uom: incoming.uom || incoming.stock_uom || 'Nos',
          stock_uom: incoming.stock_uom || 'Nos',
          item_image: incoming.item_image || null,
        };
        calcAmount(newItem);
        state.items.push(newItem);
      }
    },

    /** Remove an item by item_code. */
    removeItem(state, action) {
      state.items = state.items.filter((i) => i.item_code !== action.payload);
    },

    /**
     * Set the qty for an item. Removes the item if qty <= 0.
     * Payload: { item_code, qty }
     */
    updateQty(state, action) {
      const { item_code, qty } = action.payload;
      if (qty <= 0) {
        state.items = state.items.filter((i) => i.item_code !== item_code);
        return;
      }
      const item = state.items.find((i) => i.item_code === item_code);
      if (item) {
        item.qty = qty;
        calcAmount(item);
      }
    },

    /**
     * Set discount percentage for a line item.
     * Payload: { item_code, discount_percentage }
     */
    setLineDiscount(state, action) {
      const { item_code, discount_percentage } = action.payload;
      const item = state.items.find((i) => i.item_code === item_code);
      if (item) {
        item.discount_percentage = Math.min(100, Math.max(0, discount_percentage || 0));
        calcAmount(item);
      }
    },

    /**
     * Set note for a line item.
     * Payload: { item_code, note }
     */
    setLineNote(state, action) {
      const { item_code, note } = action.payload;
      const item = state.items.find((i) => i.item_code === item_code);
      if (item) {
        item.note = note || '';
      }
    },

    /** Set or clear the customer. */
    setCustomer(state, action) {
      state.customer = action.payload;
    },

    /** Reset the cart. */
    clearCart() {
      return initialState;
    },
  },
});

export const {
  addItem,
  removeItem,
  updateQty,
  setLineDiscount,
  setLineNote,
  setCustomer,
  clearCart,
} = cartSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectCartItems = (state) => state.cart.items;
export const selectCustomer = (state) => state.cart.customer;

export const selectCartTotals = createSelector(
  selectCartItems,
  (items) => ({
    subtotal: items.reduce((sum, i) => sum + i.amount, 0),
    itemCount: items.reduce((sum, i) => sum + i.qty, 0),
  }),
);

export default cartSlice.reducer;
